/** Matches {@link parseUrlToIndexUiState} / {@link generateUrlFromIndexUiState} jobs URL shape. */
const JOBS_SEGMENTS = new Set(["jobs", "tyopaikat", "lediga-jobb", "jobb"]);
const LOCATION_SEGMENTS = new Set(["location", "omrade", "område", "omr%C3%A5de", "alue"]);
const COMPANY_SEGMENTS = new Set(["company", "yritys", "foretag"]);
const CATEGORY_SEGMENTS = new Set(["category", "kategoria", "kategori"]);
const ROLE_SEGMENTS = new Set(["role", "ammatti", "yrke"]);

const fileExtensionPattern = /\.(map|js|css|json|png|jpg|jpeg|gif|svg|webp|ico|woff|woff2|ttf|eot)$/i;

export type IndexRouteParams = {
  locale: string;
  jobString: string;
  areaGroup: string;
  location: string;
  company: string;
  category: string;
  role: string;
};

/**
 * True when pathname should use the index SRP (+data.ts + parseUrlToIndexUiState).
 * Keeps parity with path encoding in utils/algolia/url-handling.ts.
 */
export function matchSearchIndexPath(path: string): { routeParams: IndexRouteParams } | false {
  let p = path !== "/" ? path.replace(/\/$/, "") : path;
  if (p.endsWith(".pageContext.json")) {
    p = p.slice(0, -".pageContext.json".length) || "/";
  }
  if (fileExtensionPattern.test(p)) {
    return false;
  }

  // Locale-only roots (/, /en, /fi, /sv) are handled by the landing page route.
  if (p === "/" || /^\/(en|sv|fi)$/.test(p)) {
    return false;
  }

  const segments = p.split("/").filter(Boolean);
  let i = 0;
  let locale = "en";

  if (segments[0] && ["en", "fi", "sv"].includes(segments[0].toLowerCase())) {
    locale = segments[0].toLowerCase();
    i = 1;
  }

  if (i >= segments.length) {
    return false;
  }

  const jobSeg = segments[i];
  if (!JOBS_SEGMENTS.has(jobSeg)) {
    return false;
  }
  i += 1;

  let areaGroup = "";
  let location = "";
  let company = "";
  let category = "";
  let role = "";

  if (i < segments.length && LOCATION_SEGMENTS.has(segments[i])) {
    areaGroup = segments[i];
    const locVal = segments[i + 1];
    if (!locVal || fileExtensionPattern.test(locVal)) {
      return false;
    }
    location = locVal;
    i += 2;
  }

  if (i < segments.length && COMPANY_SEGMENTS.has(segments[i].toLowerCase())) {
    const coVal = segments[i + 1];
    if (!coVal || fileExtensionPattern.test(coVal)) {
      return false;
    }
    company = coVal;
    i += 2;
  }

  if (i < segments.length && CATEGORY_SEGMENTS.has(segments[i].toLowerCase())) {
    const catVal = segments[i + 1];
    if (!catVal || fileExtensionPattern.test(catVal)) {
      return false;
    }
    category = catVal;
    i += 2;
  }

  if (i < segments.length && ROLE_SEGMENTS.has(segments[i].toLowerCase())) {
    const roleVal = segments[i + 1];
    if (!roleVal || fileExtensionPattern.test(roleVal)) {
      return false;
    }
    role = roleVal;
    i += 2;
  }

  if (i < segments.length) {
    return false;
  }

  return {
    routeParams: {
      locale,
      jobString: jobSeg,
      areaGroup,
      location,
      company,
      category,
      role,
    },
  };
}
